import React, { KeyboardEvent, useContext, useEffect, useMemo, useState } from 'react';
import { BatchActions } from '../account/AccountBatchModal';
import { AccountContext } from '../account/AccountContext';
import AccountFilterBox from '../account/AccountFilter';
import { Loading } from '../components/Loading';
import { Title } from '../components/Title';
import { FullReportEntry } from '../models/FullReportEntry';
import { ChannelIcon, ChannelLabel, ChannelReportEntry, channels } from './channel-models';
import { ChannelContext, ChannelStore, loadChannelReport, loadChannelReportByDate } from './channel-store';
import { channelNumberColumns as numberColumns, NumberColumn} from './channel-columns'
import { Column, TableRow } from '../table/Table';
import { useShortcut } from '../utils/utils';
import { ChannelAccountDetail } from './channel-account-detail';
import { DatePicker } from 'datepicker';
import { sleep } from 'simple-utils';
import { PeriodLabel } from '../account/PeriodSelector';
import { logEvent } from '../ga/ga-event-logger';

function getDateParam(date:string):string{
    if(!date)
        return null;
    let m = date.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if(m)
        return date.replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, "$3-$2-$1")
    
    return null;
}

function ChannelRender({accounts}:{accounts:FullReportEntry[]}){
    const {state: {data, date}, dispatch} =  useContext(ChannelContext);
    const {account, period} = useContext(AccountContext);
    const dateParam = useMemo(()=> getDateParam(date), [date])
    useEffect(() => {
        (async function(){
            await sleep(10);
            var data = dateParam ? (await loadChannelReportByDate(dateParam)) : (await loadChannelReport(period));
            dispatch({type:"setData", data}) 
            useEffect(function(){
                logEvent({type: "channel_report", params: {period, date}})
            }, [account])

        })();
    }, [dateParam])

    const filteredData:ChannelReportEntry[] = useMemo(()=>{
        if(!data) return null;
        return data.filter(d => accounts.find(a => d.id == a.id))
    }, [data, accounts])

    if(!data) return <Loading className='p-4' />

    if(account)
        return <ChannelAccountDetail account={account} />

    return <div className="procedure-table flex-grow-1 overflow-auto">
        <ChannelReportTable data={filteredData} />
    </div>
}

function ThSorter({col}:{col:NumberColumn}){
    const {state: {sorter}, dispatch} = useContext(ChannelContext);
    return <div className='d-flex' onClick={() => dispatch({type: 'numberSorter', id: col.id, getter: col.getter})}>
            {col.label} {sorter?.id == col.id && <i className={`ms-2 fa ${sorter.asc ? 'fa-caret-up' : 'fa-caret-down'}`}></i> }
        </div>
}

function NameSorter({col}:{col:Column<ChannelReportEntry>}){
    const {state: {sorter}, dispatch} = useContext(ChannelContext);
    return <div className='d-flex' onClick={() => dispatch({type: 'stringSorter', id: col.id, getter: a => a.name})}>
            {col.label} {sorter?.id == col.id && <i className={`ms-2 fa ${sorter.asc ? 'fa-caret-up' : 'fa-caret-down'}`}></i> }
        </div>
}

function ChannelReportTable({data}:{data:ChannelReportEntry[]}){
    const columns = useMemo(() =>  {
        return [{id:'name', label: 'Conta', render:(r) => r.name }, ...numberColumns] as Column<ChannelReportEntry>[]
    }, [])

    const {dispatch, filtered} = useContext(AccountContext);

    function selectAccount(e:ChannelReportEntry){
        var data = filtered.find(a => a.id == e.id);
        if(data)
            dispatch({type: "selectAccount", payload: data})
    }

    return <table className="table table-striped table-hover">
                <thead>
                    <tr>
                        <th rowSpan={2}>
                            <PeriodLabel short />
                            <NameSorter col={columns[0]} />
                        </th> 
                        {channels. /*filter(c => c != 'nd').*/ map(p => 
                            <th key={p} colSpan={2} className="fw-normal text-muted">
                                <i className={`${ChannelIcon[p]} me-2`}></i> {ChannelLabel[p]}
                            </th>)}
                        <th colSpan={2} className="fw-normal text-muted"><i className={`fas fa-table-list me-2`}></i> Totais</th>
                    </tr>
                    <tr>
                        {numberColumns.map(c => 
                            <th key={c.id} className={c.id.match(/leads/) ? 'col-divider' : ''}>
                            <ThSorter col={c} />
                        </th>)}
                    </tr>
                </thead>
                <tbody>
                {
                    data.map( (r, i) => <TableRow key={i} item={r} index={i} columns={columns} onClick={selectAccount} />) 
                }
                </tbody>
           </table>
        
}

function DateFilter(){
    const {state: {date}, dispatch} = useContext(ChannelContext);
    return <DatePicker date={date || ""} placeholder='Filtro por dia...' onChange={(e) => dispatch({type: "setDate", date: e})} />
}

function FilterBox(){
    const {dispatch, filters} = useContext(AccountContext);
    const [text, setText] = useState(filters?.filter ?? "");
    
    
    function doFilter(e:KeyboardEvent){
        if(e.key == "Enter"){
            dispatch({type: 'setFilter', payload: text})
        }
    }

    return <input enterKeyHint="search" 
        onKeyUp={doFilter}
    placeholder="Busca..." className="form-control" value={text} onChange={e => setText(e.target.value)} />
}


function Header(){
    const {dispatch, account} = useContext(AccountContext);

    function close(){
        if(account){
            dispatch({type: "selectAccount", payload: null})
            return;
        }

        dispatch({type: "setChannelMode", payload: false})
    }

    useShortcut("KeyC", close)

    return <div className="d-flex p-3 align-items-start procedure-page-header">
        <img src="royal-logo-white.png" className="h-light me-4" style={{opacity:.8, maxWidth:'110px', cursor:"pointer"}} onClick={close} />
        <img src="royal-logo.png" className="h-dark me-4" style={{opacity:.8, maxWidth:'110px', cursor:"pointer"}} onClick={close} />
        <h2><small className="opacity-75">Dashboard</small> Detalhes por Canal</h2>
        <div className="flex-grow-1 d-flex justify-content-end procedure-tools align-items-center">
            
            {account == null && <><DateFilter /><FilterBox /></>}
            <button className='btn btn-link' onClick={close}><i className="fa-2x fas fa-circle-xmark"></i></button>
        </div>
    </div>
}

export function ChannelTableView({accounts}:{accounts:FullReportEntry[]}){
    const {account} = useContext(AccountContext);
    
    
    return <ChannelStore>
        <div className="h-100 d-flex flex-column">
            <Header />
            <ChannelRender accounts={accounts} />
        </div>
    </ChannelStore>
}